/* Laser Medical: language preference only; never changes country or currency. */
(() => {
  'use strict';
  const script = document.currentScript;
  if (!script || script.dataset.englishEnabled !== 'true') return;
  const settings = script.dataset;
  const language = settings.locale || document.documentElement.lang || 'nl';
  // A choice in another tab (including a test tab) must not change this tab.
  // Deliberately ignore the old site-wide localStorage preference.
  const manualKey = 'lm_language_preference_v2';
  const sessionKey = 'lm_language_auto_checked_v2';
  const protectedCountries = new Set(['NL', 'BE', 'DE', 'CH', 'AT']);
  const lifetime = 180 * 24 * 60 * 60 * 1000;

  function readPreference() {
    try {
      const pref = JSON.parse(sessionStorage.getItem(manualKey) || 'null');
      return pref && ['nl', 'en'].includes(pref.language) && Date.now() - pref.savedAt < lifetime
        ? pref.language : null;
    } catch (_) { return null; }
  }

  function savePreference(value) {
    if (!['nl', 'en'].includes(value)) return;
    try { sessionStorage.setItem(manualKey, JSON.stringify({language: value, savedAt: Date.now()})); }
    catch (_) { /* A blocked preference store must not affect shopping. */ }
  }

  if (settings.designMode === 'true' || window.Shopify?.designMode) return;
  const parameters = new URLSearchParams(location.search);
  if (parameters.has('preview_theme_id') || parameters.has('preview_script_id')) return;

  document.addEventListener('change', (event) => {
    const field = event.target;
    if (field && field.name === 'language_code' && field.form && /\/localization\/?$/.test(field.form.action)) {
      savePreference(field.value);
    }
  }, true);

  if (/bot|crawler|spider|crawling|google-inspectiontool|facebookexternalhit|preview/i.test(navigator.userAgent || '')) return;
  if (language.split('-')[0] === 'en' || /^\/en(?:\/|$)/.test(location.pathname)) return;
  if (parameters.get('locale') === 'nl') { savePreference('nl'); return; }

  let interacted = false;
  const markInteraction = () => { interacted = true; };
  document.addEventListener('input', markInteraction, {capture:true, once:true});
  document.addEventListener('keydown', markInteraction, {capture:true, once:true});
  document.addEventListener('pointerdown', markInteraction, {capture:true, once:true});

  function switchToEnglish() {
    if (interacted) return;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = '/localization';
    form.hidden = true;
    const values = {
      form_type: 'localization',
      _method: 'put',
      utf8: '✓',
      language_code: 'en',
      return_to: location.pathname + location.search + location.hash
    };
    for (const [name, value] of Object.entries(values)) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  }

  const preference = readPreference();
  if (preference === 'nl') return;
  if (preference === 'en') {
    try {
      if (sessionStorage.getItem(sessionKey) === 'switching') return;
      sessionStorage.setItem(sessionKey, 'switching');
    } catch (_) { return; }
    switchToEnglish();
    return;
  }

  // Failure, missing country, or inaccessible storage leaves Dutch untouched.
  try {
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, 'checking');
  } catch (_) { return; }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 1800);
  const root = settings.root || '/';
  fetch(root.replace(/\/?$/, '/') + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', {
    credentials: 'same-origin', cache: 'no-store', signal: controller.signal
  })
    .then((response) => response.ok ? response.json() : null)
    .then((data) => {
      // Re-read after the request: a newer choice always wins.
      if (readPreference() === 'nl') return;
      const country = data?.detected_values?.country?.handle;
      if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country) || ['XX', 'ZZ'].includes(country)) return;
      if (!protectedCountries.has(country)) {
        sessionStorage.setItem(sessionKey, 'switching');
        switchToEnglish();
      }
    })
    .catch(() => {})
    .finally(() => clearTimeout(timeout));
})();
